import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import { fmtDay, formatCharge } from '../../shared/data/common';
import { useBookings } from '../../shared/booking/BookingsContext';
import { cancelHotel, getHotelBooking, quoteHotelCancellation } from '../../api/hotels';
import { useAuth } from '../../auth/AuthContext';
import { penaltyLines } from './data';

const statusText = {
  Confirmed: ['Confirmed', 'live'],
  Pending: ['Being confirmed', ''],
  CancellationPending: ['Cancellation in progress', ''],
  Cancelled: ['Cancelled', 'cancelled'],
  Failed: ['Not confirmed', 'cancelled'],
};

/**
 * Live status of a hotel booked through the hotels API: confirmation number, rooms and guests and,
 * when `allowCancel`, cancelling with the hotel.
 */
export default function HotelStay({ booking, allowCancel = false }) {
  const { auth } = useAuth();
  const { pathname } = useLocation();
  const { cancelBooking, updateBooking } = useBookings();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, details: null, error: '' });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState('');
  const supplierBookingId = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;

  useEffect(() => {
    if (!supplierBookingId) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    getHotelBooking(supplierBookingId, email, token, controller.signal)
      .then((details) => setState({ loading: false, details, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({
          loading: false,
          details: null,
          error: error.status === 404
            ? 'We could not find this booking with the hotel for these details.'
            : error.message || 'Unable to load the latest booking details.',
        });
      });
    return () => controller.abort();
  }, [supplierBookingId, email, token, attempt]);

  // Keep My Trips in step with the hotel: confirmation number, cancellation by support, or never confirmed.
  const live = state.details;
  useEffect(() => {
    if (!live || !booking) return;
    if (live.confirmationNumber && booking.ref !== live.confirmationNumber && booking.status !== 'cancelled') {
      updateBooking(booking.id, { ref: live.confirmationNumber, status: 'confirmed' });
    } else if (live.status === 'Confirmed' && booking.status === 'pending') {
      updateBooking(booking.id, { status: 'confirmed' });
    }
    if (booking.status === 'cancelled') return;
    if (live.status === 'Cancelled') cancelBooking(booking.id, booking.refund || { amount: 0, note: 'Cancelled with the hotel.' });
    if (live.status === 'Failed') cancelBooking(booking.id, { amount: booking.fare.total, note: 'The hotel could not confirm this booking, so the full amount is refunded.' });
  }, [live, booking, cancelBooking, updateBooking]);

  if (!supplierBookingId) return null;

  const heading = <h3><Icon name="hotels" size={18} /> Your stay</h3>;
  if (state.loading && !state.details) {
    return <section className="panel ins-policies" role="status">{heading}<p className="muted">Fetching the latest from the hotel…</p></section>;
  }
  if (state.error) {
    return (
      <section className="panel ins-policies">
        {heading}
        <p className="text-coral" role="alert">{state.error}</p>
        <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)}>Try again</button>
      </section>
    );
  }

  const { details } = state;
  const [label, pillClass] = statusText[details.status] || [details.status, ''];
  const lines = penaltyLines(details.cancellation, formatCharge);

  const getQuote = async () => {
    setBusy(true);
    setActionError('');
    setDone('');
    try {
      setQuote(await quoteHotelCancellation(supplierBookingId, token));
    } catch (error) {
      setActionError(error.message || 'We could not get a cancellation quote.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setActionError('');
    try {
      const result = await cancelHotel(supplierBookingId, quote.cancellationId, token);
      const note = 'Convenience fee is non-refundable.';
      if (result.bookingStatus === 'Cancelled') {
        cancelBooking(booking.id, { amount: result.customerRefund, note });
        setDone(`Cancelled. ${formatCharge(result.customerRefund)} will be refunded to your original payment method within 5–7 working days.`);
      } else {
        updateBooking(booking.id, { refund: { amount: result.customerRefund, note } });
        setDone(`Cancellation requested. The hotel is completing it — usually within a day — and ${formatCharge(result.customerRefund)} will then be refunded.`);
      }
      setQuote(null);
      setAttempt((n) => n + 1);
    } catch (error) {
      setActionError(error.status === 409
        ? error.message
        : `${error.message || 'The hotel did not confirm the cancellation.'} Your booking is still active — please contact support if you need to cancel.`);
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel ins-policies">
      <div className="fbs-head">
        {heading}
        <span className={`status-pill ${pillClass}`}>{label}</span>
      </div>
      <div className="fbs-ids">
        <div><span className="muted small">{booking.supplierCode || details.supplierCode} booking ID</span><b>{details.supplierBookingId}</b></div>
        <div><span className="muted small">Hotel confirmation no.</span><b>{details.confirmationNumber || (details.status === 'Confirmed' ? 'Use the booking ID' : 'Awaiting hotel')}</b></div>
        {details.checkIn && <div><span className="muted small">Stay</span><b>{fmtDay(details.checkIn)} – {fmtDay(details.checkOut)}</b></div>}
        <div><span className="muted small">Amount charged</span><b>{formatCharge(details.totalAmount)}</b></div>
      </div>

      {details.status === 'Failed' && (
        <div className="api-message error" role="alert">The hotel could not confirm this booking. The full amount will be refunded to your original payment method.</div>
      )}
      {details.status === 'Pending' && (
        <p className="muted small">The hotel is still confirming this booking. <button type="button" className="link small" onClick={() => setAttempt(attempt + 1)}>Refresh status</button></p>
      )}
      {details.status === 'CancellationPending' && (
        <div className="api-message">The hotel is completing your cancellation offline; this usually takes up to a day. We'll update this page once it's done.</div>
      )}

      {details.rooms.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead><tr><th>Room</th><th>Meal plan</th><th>Guests</th></tr></thead>
            <tbody>
              {details.rooms.map((room, i) => (
                <tr key={`${room.name}-${i}`}>
                  <td><b>{room.name}</b><span className="muted small block">{room.adults} adult{room.adults === 1 ? '' : 's'}{room.children ? `, ${room.children} child${room.children === 1 ? '' : 'ren'}` : ''}</span></td>
                  <td>{room.mealBasis || '—'}</td>
                  <td>{room.guests.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(details.checkInFrom || details.checkOutUntil) && (
        <p className="muted small"><Icon name="clock" size={13} /> Check-in from {details.checkInFrom || '—'} · check-out until {details.checkOutUntil || '—'}</p>
      )}

      {done && <div className="api-message success" role="status">{done}</div>}
      {actionError && <div className="api-message error" role="alert">{actionError}</div>}

      {allowCancel && ['Confirmed', 'Pending'].includes(details.status) && (
        <div className="ins-cancel">
          {lines.length > 0 && <ul className="policy-list small-list">{lines.map((l) => <li key={l}>{l}</li>)}</ul>}
          {!details.canCancel ? (
            <p className="muted small">This booking can't be cancelled online{details.status === 'Pending' ? ' until the hotel confirms it' : ' any more'}. Contact support for help.</p>
          ) : !token ? (
            <p className="muted small">
              <Link className="link" to={`/account/login?returnTo=${encodeURIComponent(pathname)}`}>Sign in</Link> with {email} to cancel this booking online.
            </p>
          ) : (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={getQuote}>
              {busy && !quote ? 'Checking refund…' : 'Cancel booking'}
            </button>
          )}
        </div>
      )}

      {quote && (
        <div className="overlay" onClick={() => !busy && setQuote(null)}>
          <div className="overlay-card modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Cancel hotel booking">
            <h3>Cancel this booking?</h3>
            <p className="muted">{details.hotelName}{details.checkIn ? ` · ${fmtDay(details.checkIn)}` : ''}</p>
            <div className="refund-box">
              <div className="summary-line"><span>Amount paid</span><span>{formatCharge(details.totalAmount)}</span></div>
              <div className="summary-line"><span>Hotel cancellation charge</span><span>{quote.penalty > 0 ? formatCharge(quote.penalty) : 'None'}</span></div>
              <div className="summary-line total"><span>Refund</span><b className="text-primary">{formatCharge(quote.customerRefund)}</b></div>
              <p className="muted small">Credited to your original payment method within 5–7 working days. The convenience fee is not refunded. This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setQuote(null)}>Keep booking</button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={confirm}>{busy ? 'Cancelling…' : 'Yes, cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
